import { useEffect, useState } from 'react'
import { useParams } from 'react-router'

import { v4 as uuidv4 } from 'uuid'

import { useHandler } from '@surglogs-app/hooks'
import { useAuthLoginTokenExternal } from '@surglogs-app/network'
import { useLocationQuery } from '@surglogs-app/routing'
import { useToast } from '@surglogs-app/ui.toast'

import { setClinicIdToStorage } from '../helpers'
import {
  getAccessTokenFromStorage,
  getLastExternalUrlTokenFromStorage,
  setLastExternalUrlTokenToStorage,
} from '../TokenSessionStorageHandler'

import { useAuthContext } from './useAuthContext'
import { useAuthToken } from './useAuthToken'
import { useClinicContext } from './useClinicContext'
import { useTokenExchangeHandler } from './useTokenExchangeHandler'

const getInitialLastUrlToken = (
  currentUrlToken: string | undefined,
): string | null => {
  const storedUrlToken = getLastExternalUrlTokenFromStorage()
  if (storedUrlToken) return storedUrlToken

  const hasStaleJwt = !!getAccessTokenFromStorage()
  return hasStaleJwt ? null : currentUrlToken ?? null
}

export const useExternalTokenAuthData = () => {
  const [isUnauthorized, setIsUnauthorized] = useState(false)
  const { token } = useLocationQuery<{ token?: string }>()
  const { token: storageToken, clearAuthData } = useAuthToken()
  const { clinicId } = useParams()
  const { showErrorToast } = useToast()
  const { changeClinic, clearClinicData } = useClinicContext()
  const { contextStore } = useAuthContext()

  const [lastUrlToken, setLastUrlToken] = useState<string | null>(() =>
    getInitialLastUrlToken(token),
  )
  const isTokenPendingExchange = !!token && token !== lastUrlToken

  const updateLastUrlToken = useHandler((value: string | null) => {
    setLastUrlToken(value)
    setLastExternalUrlTokenToStorage(value)
  })

  const handleLoginError = useHandler((e: unknown) => {
    updateLastUrlToken(token ?? null)
    setIsUnauthorized(true)
    showErrorToast({ error: e })
  })

  const onSuccess = useHandler((userId: number, t: string) => {
    updateLastUrlToken(token ?? null)
    setClinicIdToStorage(Number(clinicId), userId)
    changeClinic(Number(clinicId), t)
    contextStore.setUserId(userId)
  })

  const {
    tokenExchangeHandler,
    isPending: isTokenExchangePending,
    isSuccess: isTokenExchangeSuccess,
    reset: resetExchange,
  } = useTokenExchangeHandler({ handleLoginError, onSuccess })

  const clearStaleAuthData = useHandler(() => {
    clearAuthData()
    clearClinicData()
    resetExchange()
    setIsUnauthorized(false)
    setLastExternalUrlTokenToStorage(null)
  })

  useEffect(() => {
    if (isTokenPendingExchange) {
      clearStaleAuthData()
    }
  }, [isTokenPendingExchange, clearStaleAuthData])

  const {
    data: tokenData,
    isLoading: isLoadingExternalToken,
    isError: isExternalTokenError,
  } = useAuthLoginTokenExternal(
    {
      data: { token: token!, transaction_id: uuidv4() },
    },
    {
      opts: {
        throwOnError: false,
        enabled: !!token && !storageToken,
        gcTime: 0,
      },
    },
  )

  useEffect(() => {
    if (isExternalTokenError) {
      updateLastUrlToken(token ?? null)
      setIsUnauthorized(true)
    }
  }, [isExternalTokenError, token, updateLastUrlToken])

  const exchange = useHandler(async () => {
    await tokenExchangeHandler({
      code: tokenData!.code,
      transaction_id: tokenData!.transaction_id,
    })
  })

  useEffect(() => {
    if (
      !storageToken &&
      !isTokenExchangePending &&
      !isTokenExchangeSuccess &&
      tokenData
    ) {
      exchange()
    }
  }, [
    exchange,
    storageToken,
    isTokenExchangePending,
    isTokenExchangeSuccess,
    tokenData,
  ])

  return {
    isLoadingExternalUserAuthData:
      isLoadingExternalToken ||
      isTokenExchangePending ||
      isTokenPendingExchange,
    isUnauthorized,
  }
}
